import {useEffect, useState} from 'react'
import {
  Breadcrumb,
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Form,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap'
import {useLazyQuery, useMutation} from '@apollo/client'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {GET_ALL_PDF_DOCUMENT_TEMPLATES} from '../../gql/queries/pdfEnvelopesApiQueries'
import {UPDATE_PDF_DOCUMENT_TEMPLATE, DELETE_PDF_DOCUMENT_TEMPLATE} from '../../gql/mutations/pdfEnvelopesMutations'
import {IResAllPdfDocumentTemplates, NodePdfDocumentTemplate} from '../../types/pdfEnvelopesApi'
import {parseId, hasPermission} from '../../helpers'
import {ALL_PERMISSIONS, SIZE_PAGE_DEFAULT} from '../../helpers/consts'
import {useGetUser} from '../../store/userStore'
import {useDisclourse} from '../../hooks/useDisclourse'
import {PaginationUtils} from '../shared/PaginationUtils'
import {PaginationSection} from '../shared/PaginationSection'
import moment from 'moment'
import {BadgeStatus} from './components/BadgeStatus'
import {ModalAddPdfTemplate} from './components/ModalAddPdfTemplate'
import {ModalPdfFieldEditor} from './components/ModalPdfFieldEditor'
import {ModalEnvelopePreview} from './components/ModalEnvelopePreview'
import {ModalRequestEnvelope} from './components/ModalRequestEnvelope'

function parseJsonLen(val: any): number {
  if (!val) return 0
  if (Array.isArray(val)) return val.length
  if (typeof val === 'string') {
    try { return JSON.parse(val).length } catch { return 0 }
  }
  return 0
}

export const PdfEnvelopesPage = () => {
  const navigate = useNavigate()
  const permissions = useGetUser((s) => s.user?.permissions)
  const canAdd = permissions && hasPermission(permissions, ALL_PERMISSIONS.ADD_PDF_ENVELOPES)
  const canEdit = permissions && hasPermission(permissions, ALL_PERMISSIONS.EDIT_PDF_ENVELOPES)

  const [gqlGetTemplates, {data, loading, error, refetch}] =
    useLazyQuery<IResAllPdfDocumentTemplates>(GET_ALL_PDF_DOCUMENT_TEMPLATES, {
      fetchPolicy: 'no-cache',
    })

  const [updateTemplate] = useMutation(UPDATE_PDF_DOCUMENT_TEMPLATE)
  const [deleteTemplate] = useMutation(DELETE_PDF_DOCUMENT_TEMPLATE)

  const {isOpen: isOpenAdd, onOpen: onOpenAdd, onClose: onCloseAdd} = useDisclourse()
  const {isOpen: isOpenEditor, onOpen: onOpenEditor, onClose: onCloseEditor} = useDisclourse()
  const {isOpen: isOpenPreview, onOpen: onOpenPreview, onClose: onClosePreview} = useDisclourse()
  const {isOpen: isOpenRequest, onOpen: onOpenRequest, onClose: onCloseRequest} = useDisclourse()

  const [selectedTemplate, setSelectedTemplate] = useState<NodePdfDocumentTemplate | null>(null)
  const [selectedEnvelopeId] = useState<string>('')
  const [nameFilter, setNameFilter] = useState('')
  type IFilterStatus = 'all' | 'DRAFT' | 'ACTIVE' | 'INACTIVE'
  const [filterStatus, setFilterStatus] = useState<IFilterStatus>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)
  const [dataArr, setDataArr] = useState<{node: NodePdfDocumentTemplate}[]>([])

  const total = data?.allPdfDocumentTemplates?.totalCount ?? 0
  const totalPages = Math.ceil(total / first)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, first, total)

  const loadTemplates = () => {
    gqlGetTemplates({
      variables: {
        first,
        offset: (currentPage - 1) * first,
        name: nameFilter || null,
        status: filterStatus === 'all' ? null : filterStatus,
      },
    })
  }

  useEffect(() => {
    loadTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, currentPage])

  useEffect(() => {
    if (data) setDataArr(data.allPdfDocumentTemplates.edges)
  }, [data])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  const handleStatusChange = async (id: string, status: string) => {
    try {
      const {data} = await updateTemplate({variables: {id: parseId(id), status}})
      if (data?.pdfTemplateUpdate?.ok) {
        toast.success(`Template marked as ${status.toLowerCase()}`)
        refetch()
      } else {
        toast.error(data?.pdfTemplateUpdate?.errors?.[0]?.message || 'Error updating')
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete template "${name}"?`)) return
    try {
      const {data} = await deleteTemplate({variables: {id: parseId(id)}})
      if (data?.pdfTemplateDelete?.ok) {
        toast.success('Template deleted')
        refetch()
      } else {
        toast.error(data?.pdfTemplateDelete?.errors?.[0]?.message || 'Error deleting')
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  return (
    <div className='signatureForms-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='fs-3'>PDF Signing</Breadcrumb.Item>
        </Breadcrumb>
        <div className='d-flex align-items-center gap-2'>
          {canAdd && (
            <Button size='sm' onClick={onOpenAdd}>
              <i className='bi bi-plus fs-3' /> New PDF Template
            </Button>
          )}
        </div>
      </header>

      <Row className='signatureForms'>
        <Col className='container'>
          <div className='signatureForms-container'>
            <div className='title-header'>
              <h3>PDF Document Templates</h3>
            </div>

            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  placeholder='Search by name...'
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') loadTemplates()
                  }}
                />
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as IFilterStatus)}
                >
                  <option value='all'>All statuses</option>
                  <option value='DRAFT'>Draft</option>
                  <option value='ACTIVE'>Active</option>
                  <option value='INACTIVE'>Inactive</option>
                </Form.Select>
                <Button variant='outline-success' style={{border: '1px solid #dbdfe9'}} onClick={loadTemplates}>
                  Search
                </Button>
              </InputGroup>
            </div>

            <div className='results-header'>
              <PaginationSection
                first={first}
                setFirst={(n) => {setFirst(n); setCurrentPage(1)}}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={total}
                updatePage={(n) => setCurrentPage(n)}
                PaginationUtils={PaginationUtils}
              />
            </div>

            <div className='table-responsive table table-striped table-hover'>
              <table className='table-scrollable table-scroll table table-striped table-hover' style={{whiteSpace: 'nowrap'}}>
                <thead>
                  <tr>
                    <th className='fw-bold'>Name</th>
                    <th className='fw-bold'>Status</th>
                    <th className='fw-bold'>Roles</th>
                    <th className='fw-bold'>Fields</th>
                    <th className='fw-bold'>Envelopes</th>
                    <th className='fw-bold'>Created</th>
                    <th className='fw-bold'>Actions</th>
                  </tr>
                </thead>
                {loading ? (
                  <tbody>
                    <tr>
                      <td colSpan={7}>
                        <div className='d-flex flex-center' style={{height: '30vh'}}>
                          <Spinner animation='border' />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {dataArr.length === 0 && (
                      <tr>
                        <td colSpan={7}>
                          <div className='d-flex flex-center' style={{height: '20vh'}}>
                            No PDF templates found. Create one.
                          </div>
                        </td>
                      </tr>
                    )}
                    {dataArr.map(({node}) => (
                      <tr key={node.id}>
                        <td>
                          <span
                            className='text-primary'
                            style={{cursor: 'pointer'}}
                            onClick={() => {
                              setSelectedTemplate(node)
                              onOpenEditor()
                            }}
                          >
                            {node.name}
                          </span>
                        </td>
                        <td><BadgeStatus state={node.status} /></td>
                        <td>{parseJsonLen(node.signerRoles)}</td>
                        <td>{parseJsonLen(node.fieldDefinitions)}</td>
                        <td>{node.envelopesCount ?? 0}</td>
                        <td>{node.created ? moment(node.created).format('DD MMM, YY') : '—'}</td>
                        <td>
                          <Dropdown as={ButtonGroup}>
                            <Button
                              variant='outline-primary'
                              size='sm'
                              style={{border: '1px solid #3e97ff'}}
                              onClick={() => {
                                setSelectedTemplate(node)
                                onOpenEditor()
                              }}
                            >
                              Editor
                            </Button>
                            <Dropdown.Toggle
                              split
                              variant='outline-primary'
                              size='sm'
                              id={`dd-${node.id}`}
                              style={{border: '1px solid #3e97ff'}}
                            />
                            <Dropdown.Menu>
                              {node.status === 'ACTIVE' && (
                                <Dropdown.Item
                                  onClick={() => {
                                    setSelectedTemplate(node)
                                    onOpenRequest()
                                  }}
                                >
                                  <i className='bi bi-send-fill' /> Send for signature
                                </Dropdown.Item>
                              )}
                              {canEdit && node.status !== 'ACTIVE' && (
                                <Dropdown.Item onClick={() => handleStatusChange(node.id, 'ACTIVE')}>
                                  <i className='bi bi-check-square-fill' /> Activate
                                </Dropdown.Item>
                              )}
                              {canEdit && node.status === 'ACTIVE' && (
                                <Dropdown.Item onClick={() => handleStatusChange(node.id, 'INACTIVE')}>
                                  <i className='bi bi-folder-fill' /> Deactivate
                                </Dropdown.Item>
                              )}
                              <Dropdown.Item
                                onClick={() => navigate(`/pdf-envelopes/${parseId(node.id)}/envelopes`)}
                              >
                                <i className='bi bi-list-check' /> View Envelopes
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item
                                className='text-danger'
                                onClick={() => handleDelete(node.id, node.name)}
                              >
                                <i className='bi bi-trash' /> Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpenAdd && (
        <ModalAddPdfTemplate
          isOpen={isOpenAdd}
          onClose={onCloseAdd}
          refetch={refetch}
          onTemplateCreated={(id) => {
            // After creation, open editor for field placement
            const newTemplate = data?.allPdfDocumentTemplates?.edges?.find(
              (e) => parseId(e.node.id) === id
            )?.node
            if (newTemplate) {
              setSelectedTemplate(newTemplate)
              onOpenEditor()
            }
          }}
        />
      )}

      {isOpenEditor && selectedTemplate && (
        <ModalPdfFieldEditor
          template={selectedTemplate}
          isOpen={isOpenEditor}
          onClose={onCloseEditor}
          refetch={refetch}
        />
      )}

      {isOpenPreview && selectedEnvelopeId && (
        <ModalEnvelopePreview
          envelopeId={selectedEnvelopeId}
          isOpen={isOpenPreview}
          onClose={onClosePreview}
          refetch={refetch}
        />
      )}

      {isOpenRequest && (
        <ModalRequestEnvelope
          isOpen={isOpenRequest}
          onClose={onCloseRequest}
          preSelectedTemplateId={selectedTemplate?.id}
          refetch={refetch}
        />
      )}
    </div>
  )
}
